import { AlertTriangle, BarChart3, Bell, BookOpenCheck, Boxes, CalendarDays, CalendarRange, ClipboardCheck, CloudOff, FileCheck2, Gauge, History, ListChecks, LockKeyhole, LogOut, Mail, Menu, RefreshCw, Smartphone, Upload, Users, X } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useSync } from '../offline/SyncContext'
import { getQueueSummary } from '../offline/outbox'
import { Button, ConfirmDialog, Modal } from './ui'
import { RouteScrollRestoration } from './RouteScrollRestoration'
import { errorMessage, roleLabel } from '../utils/format'

const navigation = [
  { to: '/', label: 'Обзор', icon: Gauge, end: true },
  { to: '/work-orders', label: 'Мои задания', plannerLabel: 'Задания', icon: ListChecks },
  { to: '/calendar', label: 'Календарь работ', icon: CalendarDays, roles: ['ROLE_PLANNER'] },
  { to: '/plans', label: 'Планы работ', icon: CalendarRange, roles: ['ROLE_PLANNER'] },
  { to: '/analytics', label: 'Аналитика', icon: BarChart3, roles: ['ROLE_ADMIN', 'ROLE_SUPERVISOR'] },
  { to: '/defects', label: 'Дефекты', icon: AlertTriangle },
  { to: '/reports', label: 'Журналы и отчёты', icon: FileCheck2, roles: ['ROLE_PLANNER'] },
  { to: '/imports', label: 'Документы и ППР', icon: Upload, roles: ['ROLE_PLANNER'] },
  { to: '/equipment', label: 'Оборудование', icon: Boxes },
  { to: '/programs', label: 'Программы ТО', icon: ClipboardCheck, roles: ['ROLE_PLANNER'] },
  { to: '/catalog', label: 'Объекты и справочники', icon: BookOpenCheck, roles: ['ROLE_DATA_EDITOR'] },
  { to: '/audit-log', label: 'История изменений', icon: History, roles: ['ROLE_PLANNER'] },
  { to: '/admin/users', label: 'Команда и роли', icon: Users, roles: ['ROLE_TEAM_MANAGER'] },
  { to: '/devices', label: 'Устройства', icon: Smartphone, roles: ['ROLE_TEAM_MANAGER'] },
  { to: '/sync', label: 'Сохранённые изменения', icon: RefreshCw },
  { to: '/notifications', label: 'Уведомления', icon: Bell },
]

export function AppShell() {
  const { user, activeRole, authExpired, logout, switchRole, hasAnyRole } = useAuth()
  const sync = useSync()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileViewport, setMobileViewport] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches)
  const [logoutDescription, setLogoutDescription] = useState('')
  const [loggingOut, setLoggingOut] = useState(false)
  const [updateReady, setUpdateReady] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const planner = hasAnyRole('ROLE_PLANNER')
  const visibleNavigation = navigation.filter((item) => !item.roles || hasAnyRole(...item.roles)).map((item) => ({ ...item, label: planner && item.plannerLabel ? item.plannerLabel : item.label }))

  useEffect(() => {
    const initialController = navigator.serviceWorker?.controller
    if (!initialController) return
    const onControllerChange = () => setUpdateReady(true)
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
  }, [])

  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)')
    const updateViewport = (event: MediaQueryListEvent) => {
      setMobileViewport(event.matches)
      if (!event.matches) setMobileOpen(false)
    }
    media.addEventListener('change', updateViewport)
    return () => media.removeEventListener('change', updateViewport)
  }, [])

  useEffect(() => {
    if (!mobileViewport || !mobileOpen) return

    const menuButton = menuButtonRef.current
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : menuButton
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const focusableElements = () => [...(sidebarRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [])]
      .filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true')

    focusableElements()[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setMobileOpen(false)
        return
      }
      if (event.key !== 'Tab') return

      const focusable = focusableElements()
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      const returnTarget = returnFocusRef.current?.isConnected ? returnFocusRef.current : menuButton
      window.requestAnimationFrame(() => returnTarget?.focus())
    }
  }, [mobileOpen, mobileViewport])

  async function signOut() {
    if (!user) return
    const summary = await getQueueSummary(user.id)
    const confirmation = summary.total > 0
      ? `На устройстве есть ${summary.total} неотправленных действий. При выходе они будут удалены. Всё равно выйти?`
      : 'Выйти? Сохранённые на этом устройстве данные учётной записи будут удалены.'
    setLogoutDescription(confirmation)
  }

  async function confirmSignOut() {
    setLoggingOut(true)
    try {
      await logout()
      setLogoutDescription('')
    } finally {
      setLoggingOut(false)
    }
  }

  const currentTitle = location.pathname === '/scan' ? 'Сканирование QR' : visibleNavigation.find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))?.label || 'Управление ТОиР'

  return (
    <div className="app-shell">
      <RouteScrollRestoration />
      <aside
        id="primary-navigation-drawer"
        ref={sidebarRef}
        className={`sidebar ${mobileOpen ? 'sidebar--open' : ''}`}
        aria-label="Основная навигация"
        aria-hidden={mobileViewport && !mobileOpen ? true : undefined}
        inert={mobileViewport && !mobileOpen}
      >
        <div className="brand"><span className="brand__mark"><img src="/brand/logo.png" alt="" width="36" height="36" /></span><span><strong>Управление ТОиР</strong><small>ООО «БТТ-ГРУПП»</small></span></div>
        <nav className="sidebar__nav">
          {visibleNavigation.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setMobileOpen(false)} className={({ isActive }) => isActive ? 'nav-link nav-link--active' : 'nav-link'}>
              <Icon size={19} aria-hidden="true" /><span>{label}</span>
              {to === '/sync' && (sync.pendingCount + sync.attentionCount) > 0 && <span className="nav-link__count">{sync.pendingCount + sync.attentionCount}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <div className="user-summary"><span className="avatar">{user?.fullName?.charAt(0) || user?.login?.charAt(0) || 'П'}</span><span><strong>{user?.fullName || user?.login || user?.email}</strong>{(user?.roles.length || 0) > 1 ? <select name="activeRole" className="role-switch" aria-label="Рабочая роль" value={activeRole || ''} onChange={(event) => void switchRole(event.target.value)}>{user?.roles.map((role) => <option key={role} value={role}>{roleLabel([role])}</option>)}</select> : <small>{roleLabel(activeRole ? [activeRole] : user?.roles || [])}</small>}</span></div>
          <button className="logout-button" onClick={() => { setMobileOpen(false); void signOut() }}><LogOut size={17} />Выйти</button>
        </div>
      </aside>
      {mobileOpen && <button className="sidebar-overlay" onClick={() => setMobileOpen(false)} aria-label="Закрыть меню" />}
      <div className="app-main">
        <header className="topbar">
          <button ref={menuButtonRef} className="mobile-menu" onClick={() => setMobileOpen((open) => !open)} aria-controls="primary-navigation-drawer" aria-expanded={mobileOpen} aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}>{mobileOpen ? <X /> : <Menu />}</button>
          <div><p className="eyebrow">Управление ТОиР</p><h1>{currentTitle}</h1></div>
          <NavLink to="/sync" aria-label={sync.browserOnline ? (sync.syncing ? 'Отправляем сохранённые изменения' : 'Есть подключение. Открыть сохранённые изменения') : 'Работа без сети. Открыть сохранённые изменения'} className={`connection ${sync.browserOnline ? 'connection--online' : 'connection--offline'}`}>
            {sync.browserOnline ? <RefreshCw className={sync.syncing ? 'spin' : ''} size={17} /> : <CloudOff size={17} />}
            <span>{sync.browserOnline ? (sync.syncing ? 'Отправляем изменения' : 'Есть подключение') : 'Работа без сети'}</span>
            {sync.pendingCount > 0 && <b>{sync.pendingCount}</b>}
          </NavLink>
        </header>
        {!sync.browserOnline && <div className="offline-banner" role="status"><CloudOff size={17} /><span>Можно продолжать работу. Изменения сохранятся на устройстве и отправятся после подключения.</span></div>}
        {updateReady && <div className="offline-banner" role="status"><RefreshCw size={17} /><span>Доступна новая версия приложения.</span><Button variant="secondary" onClick={() => window.location.reload()}>Обновить страницу</Button></div>}
        <main className="page-container"><Outlet /></main>
        <nav className="bottom-nav" aria-label="Мобильная навигация">
          {visibleNavigation.slice(0, 4).map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} aria-label={label}><Icon size={20} /><span>{label === 'Документы и ППР' ? 'Документы' : label === 'Месячный план' ? 'План' : label === 'Календарь работ' ? 'Календарь' : label === 'Мои задания' ? 'Задания' : label === 'Оборудование' ? 'Реестр' : label}</span></NavLink>)}
        </nav>
        <footer className="app-footer">Управление ТОиР · планирование, исполнение и контроль работ</footer>
      </div>
      {authExpired && user && !logoutDescription && <ReauthenticationDialog key={user.id} onLogout={signOut} />}
      <ConfirmDialog open={Boolean(logoutDescription)} title="Выйти из системы?" description={logoutDescription} confirmLabel="Выйти и удалить данные" tone="danger" loading={loggingOut} onClose={() => setLogoutDescription('')} onConfirm={() => void confirmSignOut()} />
    </div>
  )
}

function ReauthenticationDialog({ onLogout }: { onLogout(): Promise<void> }) {
  const { user, reauthenticate } = useAuth()
  const [identifier, setIdentifier] = useState(user?.login || user?.email || '')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await reauthenticate(identifier.trim(), password)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      dismissible={false}
      size="small"
      title="Войдите снова"
      description="Неотправленные действия сохранены на этом устройстве. Войдите в ту же учётную запись, и отправка продолжится."
      onClose={() => undefined}
    >
      <form onSubmit={(event) => void submit(event)}>
        <div className="modal__body reauth-fields">
          {error && <div className="form-message form-message--error" role="alert">{error}</div>}
          <label className="field"><span>Логин или рабочая почта</span><div className="field__control"><Mail size={18} /><input name="login" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} value={identifier} onChange={(event) => setIdentifier(event.target.value)} required /></div></label>
          <label className="field"><span>Пароль</span><div className="field__control"><LockKeyhole size={18} /><input name="password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required autoFocus data-autofocus /></div></label>
        </div>
        <div className="modal__footer reauth-actions">
          <Button type="button" variant="quiet" onClick={() => void onLogout()}>Выйти и удалить данные</Button>
          <Button type="submit" loading={submitting} disabled={!navigator.onLine || !identifier.trim() || !password}>Продолжить отправку</Button>
        </div>
      </form>
    </Modal>
  )
}
